import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useStores } from '~/hooks/useStore';
import { mainStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';

import { SUPER_MENU_DROPDOWN_KEY } from '../CatalogButton/constants';

type CategoriesSubheaderProps = {
  className?: string;
};

const chipBaseClasses =
  'inline-flex h-7 cursor-pointer items-center justify-center rounded-full px-3 py-1 whitespace-nowrap select-none transition-colors duration-[256ms] ease-main';

const chipInactiveClasses =
  'border border-navigation-stroke-secondary bg-navigation-bg text-navigation-text-secondary hover:bg-surface-transparent-2';

const CategoriesSubheader: React.FC<CategoriesSubheaderProps> = observer(
  ({ className }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { shopCategories, homeCategories } = useStores();
    const { handleClickCategory } = useCategoryAnalytics();

    if (mainStore.isHeaderDropdownOpen(SUPER_MENU_DROPDOWN_KEY)) return null;

    const fromShopSubcategories = shopCategories.list.reduce<Category[]>(
      (acc, item) => acc.concat(item?.subcategory ?? []),
      [],
    );

    let categories: Category[] = fromShopSubcategories;
    if (categories.length === 0) {
      categories = [...shopCategories.list];
    }
    if (categories.length === 0) {
      categories = [...homeCategories.list];
    }

    return (
      <div
        className={cn(
          'flex w-full gap-2 overflow-x-auto scrollbar-hide',
          className,
        )}
      >
        <button
          type="button"
          data-link="subheader-category-all"
          onClick={() => navigate('/shop')}
          className={cn(
            chipBaseClasses,
            chipInactiveClasses,
            'ml-4 shrink-0',
            categories.length === 0 && 'mr-4',
          )}
        >
          <Typography.Text200Medium as="span">
            {t('header:all')}
          </Typography.Text200Medium>
        </button>
        {categories.map(({ id, name }, index) => {
          const isLast = index === categories.length - 1;
          return (
            <button
              key={id}
              type="button"
              data-link={`subheader-category-${id}`}
              onClick={() => {
                handleClickCategory(id, 'subheader')();
                navigate(`/c/${id}`);
              }}
              className={cn(
                chipBaseClasses,
                chipInactiveClasses,
                'shrink-0',
                isLast && 'mr-4',
              )}
            >
              <Typography.Text200Medium as="span">
                {name}
              </Typography.Text200Medium>
            </button>
          );
        })}
      </div>
    );
  },
);
CategoriesSubheader.displayName = 'CategoriesSubheader';

export { CategoriesSubheader };
